/**
 * AsciiEngine — top-level controller for the Bestiary's ASCII layer.
 *
 * Owns the canvas + a list of active effects + the requestAnimationFrame
 * loop. The timeline calls into it via three methods:
 *
 *   await ascii.loadScene({ asciiUrl, animationScript })
 *   await ascii.runReveal()
 *   ascii.startSustained()
 *   await ascii.runDeparture()
 *
 * Animation script schema (per-entry JSON):
 *   {
 *     reveal:   { type: "decode_from_noise", durationMs: 4000, pattern: "center_out" },
 *     sustained: [
 *       { type: "ambient_flicker", intensity: 0.3 },
 *       { type: "density_pulse", bounds: {...}, rateMs: 3000 },
 *       { type: "particles", spawnRegion: {...}, direction: "down", chars: [".","'"], ... },
 *       ...
 *     ],
 *     departure: { type: "fade_to_noise", durationMs: 2000 }
 *   }
 */

import { AsciiCanvas } from "./canvas.js";
import { makeEffect } from "./effects.js";

// Default dimensions for 16:9 visual aspect at typical character cell
// ratio (~0.55). 450×135 gives ~5× more sample points than the original
// 200×60. The renderer is HTML5 Canvas2D with diff-based redraw —
// only cells whose char-or-color changed since the last frame get
// repainted, so per-frame work is O(animated cells) rather than
// O(total cells). Plenty of headroom for further bumps to 600+ cols
// before performance becomes a concern.
export const DEFAULT_COLS = 600;
export const DEFAULT_ROWS = 180;

export function initAsciiEngine(rootEl, opts = {}) {
  return new AsciiEngine(rootEl, opts);
}

class AsciiEngine {
  constructor(rootEl, { cols = DEFAULT_COLS, rows = DEFAULT_ROWS } = {}) {
    this.rootEl = rootEl;
    this.cols = cols;
    this.rows = rows;

    // Build the <canvas> element where the painting renders. Switched
    // from <pre>+innerHTML to Canvas2D for diff-based redraw — same
    // visual output, ~10× headroom for grid resolution and animation
    // density.
    this.canvasEl = document.createElement("canvas");
    this.canvasEl.className = "ascii-canvas";
    this.canvasEl.setAttribute("aria-hidden", "true");
    this.rootEl.appendChild(this.canvasEl);

    this.canvas = new AsciiCanvas(this.canvasEl, cols, rows);
    this.activeEffects = [];
    this.animating = false;
    this.lastFrameTime = 0;
    this._rafId = null;
  }

  /**
   * Load a scene's ASCII source + animation script.
   * Replaces any current state.
   */
  async loadScene({ asciiUrl, animationScript }) {
    if (asciiUrl) {
      try {
        const r = await fetch(asciiUrl);
        if (r.ok) {
          const text = await r.text();
          // Vite's dev-server falls back to index.html on 404, which would
          // load HTML as our ASCII art. Reject anything that smells like HTML.
          const trimmed = text.trim();
          if (trimmed.startsWith("<") || trimmed.toLowerCase().includes("<!doctype")) {
            console.warn(
              `[ascii] ${asciiUrl} returned HTML (probably 404 → index.html fallback). Using placeholder.`,
            );
            this._loadPlaceholder();
          } else {
            this.canvas.loadSource(text);
          }
        } else {
          console.warn(`[ascii] failed to load ${asciiUrl}: ${r.status}`);
          this._loadPlaceholder();
        }
      } catch (e) {
        console.warn(`[ascii] error loading ${asciiUrl}: ${e}`);
        this._loadPlaceholder();
      }
    } else {
      this._loadPlaceholder();
    }
    this.canvas.resetToSource();
    this.script = animationScript || {};
    // Apply per-cell accent regions before the first render so the
    // initial paint is colored correctly, not a flash of all-sepia.
    this.canvas.setAccents(this.script.accents || []);
    this.canvas.render();
  }

  /** Run the reveal animation. Resolves when complete.
   *
   * Each entry begins with a decode-from-noise: the painting region
   * fills with random noise glyphs, then resolves cell-by-cell into
   * the source ASCII art over ~1.8 seconds. Pattern is "random" by
   * default — feels like film grain settling. Per-entry overrides
   * can be specified via the `reveal` field in the animation script.
   *
   * Wrapped in try/catch so a buggy effect can't strand the timeline.
   */
  async runReveal() {
    const desc = this.script?.reveal ?? {
      type: "decode_from_noise",
      durationMs: 2400,
      pattern: "random",
    };
    let effect;
    try {
      effect = makeEffect(this.canvas, desc);
    } catch (e) {
      console.warn("[ascii] reveal makeEffect threw, skipping:", e);
    }
    if (!effect) {
      this.canvas.resetToSource();
      this.canvas.render();
      return;
    }
    // Reveal must run WITHOUT sustained effects ticking — sustained
    // effects like PaintingBreath only rewrite cells when their sine
    // delta changes step (~4× per cycle), so DecodeFromNoise's
    // every-frame noise writes win the race and the painting visibly
    // pulses back to noise. Run the reveal in isolation.
    await this._runEffectToCompletion(effect, { skipSustained: true });
    // Final clean state — guarantees the painting is at source
    // regardless of how the effect ended.
    this.canvas.resetToSource();
    this.canvas.render();
  }

  /** Start the sustained effects (regions, particles, flicker). */
  startSustained() {
    const sustained = this.script?.sustained || [];
    for (const desc of sustained) {
      const effect = makeEffect(this.canvas, desc);
      if (effect) {
        effect.start();
        this.activeEffects.push(effect);
      }
    }
    this._startLoop();
  }

  /** Stop sustained effects. */
  stopSustained() {
    this.activeEffects = [];
  }

  /** Run the departure animation. Resolves when complete. */
  async runDeparture() {
    this.stopSustained();
    const desc = this.script?.departure;
    if (!desc) {
      this.canvas.clear();
      this.canvas.render();
      return;
    }
    const effect = makeEffect(this.canvas, desc);
    if (!effect) return;
    await this._runEffectToCompletion(effect);
    this.canvas.clear();
    this.canvas.render();
  }

  /** Clear and stop everything. Useful on reset. */
  reset() {
    this.stopSustained();
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this.canvas.clear();
    this.canvas.render();
  }

  // ─── Internal loop ────────────────────────────────────────────────

  _startLoop() {
    if (this.animating) return;
    this.animating = true;
    this.lastFrameTime = performance.now();
    const tick = (now) => {
      if (!this.animating) return;
      const dt = now - this.lastFrameTime;
      this.lastFrameTime = now;
      for (const effect of this.activeEffects) {
        effect.update(dt);
      }
      this.canvas.render();
      this._rafId = requestAnimationFrame(tick);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  _stopLoop() {
    this.animating = false;
    if (this._rafId) cancelAnimationFrame(this._rafId);
    this._rafId = null;
  }

  async _runEffectToCompletion(effect, { skipSustained = false } = {}) {
    try {
      effect.start();
    } catch (e) {
      console.warn("[ascii] effect.start() threw; skipping:", e);
      return;
    }
    return new Promise((resolve) => {
      // Local time tracker. We can NOT share this.lastFrameTime with
      // _startLoop — once both rAF chains are running concurrently
      // (scene II onward, since _startLoop is never stopped between
      // scenes), they fight for the field. Whichever tick runs first
      // each frame steals the dt budget and the other gets dt=0.
      // For the reveal that's fatal: DecodeFromNoise.elapsed never
      // advances and the painting stays stuck in noise. Using a
      // local lastFrameTime decouples the two loops.
      let lastFrameTime = performance.now();
      const tick = (now) => {
        const dt = now - lastFrameTime;
        lastFrameTime = now;
        try {
          // Run the focused effect AND optionally any sustained ones.
          // For the reveal we pass skipSustained=true: PaintingBreath
          // and AmbientFlicker only rewrite cells when their internal
          // state changes (~4× per cycle), so DecodeFromNoise's noise
          // writes win every frame and the painting visibly pulses
          // back to noise. The reveal needs to own the canvas.
          //
          // Any throw inside update() must NOT leave this Promise
          // unresolved — that would hang the timeline forever (e.g.
          // a click on entry I triggers ascii.runDeparture, which
          // awaits this; if update throws, Promise.all in playScene
          // never settles and the next scene never starts).
          effect.update(dt);
          if (!skipSustained) {
            for (const sustained of this.activeEffects) {
              sustained.update(dt);
            }
          }
          this.canvas.render();
        } catch (e) {
          console.warn("[ascii] effect.update() threw; aborting:", e);
          resolve();
          return;
        }
        if (effect.done()) {
          resolve();
        } else {
          this._rafId = requestAnimationFrame(tick);
        }
      };
      this._rafId = requestAnimationFrame(tick);
    });
  }

  /**
   * Build a placeholder ASCII when no source is available yet. Useful
   * for development before the pipeline has produced real output.
   */
  _loadPlaceholder() {
    const { cols, rows } = this;
    const lines = new Array(rows);
    const title = "[ ASCII PLACEHOLDER ]";
    for (let y = 0; y < rows; y++) {
      let line = "";
      for (let x = 0; x < cols; x++) {
        const onBorder =
          x === 0 || x === cols - 1 || y === 0 || y === rows - 1;
        const onTitle =
          y === Math.floor(rows / 2) &&
          x >= Math.floor((cols - title.length) / 2) &&
          x < Math.floor((cols - title.length) / 2) + title.length;
        if (onTitle) {
          line += title[x - Math.floor((cols - title.length) / 2)];
        } else if (onBorder) {
          line += "·";
        } else {
          line += " ";
        }
      }
      lines[y] = line;
    }
    this.canvas.loadSource(lines.join("\n"));
  }
}
